// Number and time display (plan 9.1.4) — the mockup App.fmt, per language. In components use useFmt() (src/i18n).
import { DAY, TIME_ZONE, parseYmd } from './time';

export type Lang = 'ko' | 'en';
export type TFn = (key: string, params?: Record<string, unknown>) => string;

const locale = (lang: Lang) => (lang === 'en' ? 'en-US' : 'ko-KR');
const dtfCache = new Map<string, Intl.DateTimeFormat>();
function formatter(lang: Lang, key: string, opts: Intl.DateTimeFormatOptions, timezone: string): Intl.DateTimeFormat {
  const ck = lang + key + timezone;
  let f = dtfCache.get(ck);
  if (!f) {
    f = new Intl.DateTimeFormat(locale(lang), { ...opts, timeZone: timezone });
    dtfCache.set(ck, f);
  }
  return f;
}

export function fmtInt(n: number, lang: Lang): string {
  return Number(n).toLocaleString(locale(lang));
}
/** Fixed decimals, trailing ".0" removed, then grouped (mockup trim()). */
function trim(x: number, d: number, lang: Lang): string {
  let v = Number(x).toFixed(d);
  if (d) v = v.replace(/\.0+$/, '');
  return Number(v).toLocaleString(locale(lang), { maximumFractionDigits: d });
}

export interface Fmt {
  lang: Lang;
  int(n: number | null | undefined): string;
  num(n: number | null | undefined, d?: number): string;
  /** ko 1.2만·3.4억 / en 12.5K·1.2M — under 10,000 (ko) or 1,000 (en) stays exact with grouping */
  compact(n: number | null | undefined): string;
  tok(n: number | null | undefined): string;
  usd(x: number | null | undefined): string;
  pct(x: number | null | undefined, d?: number): string;
  /** 0.05초 · 2.3초 · 12분 30초 · 1시간 20분 (no milliseconds) */
  dur(ms: number | null | undefined): string;
  date(ts: number): string;
  dateShort(ts: number): string;
  dateYear(ts: number): string;
  time(ts: number): string;
  clock(ts: number): string;
  dateTime(ts: number): string;
  full(ts: number): string;
  /** 'YYYY-MM-DD HH:mm' in Korea time whatever the language (access watermark) */
  stamp(ts: number): string;
  /** 방금 · 3분 전 · 2시간 전 · 어제 · 5일 전 · then a short date */
  rel(ts: number): string;
  range(from: number, to: number): string;
  md(ts: number): string;
  /** A date without a time zone; never shifted by the personal display preference. */
  calendarDate(day: string, style?: 'year' | 'md' | 'date'): string;
  /** 'YYYY-MM-DD' pair → "10월 1일~10월 20일 · 20일" */
  rangeSum(from: string, to: string): string;
  bytes(gb: number): string;
}

export function createFmt(lang: Lang, t: TFn, now: () => number = Date.now, timezone = TIME_ZONE, numberUnit: 'compact' | 'full' = 'compact'): Fmt {
  const dtf = (language: Lang, key: string, opts: Intl.DateTimeFormatOptions) => formatter(language, key, opts, timezone);
  const en = lang === 'en';
  const f: Fmt = {
    lang,
    int: (n) => (n == null ? '' : fmtInt(Math.round(n), lang)),
    num: (n, d) => (n == null ? '' : Number(n).toLocaleString(locale(lang), { minimumFractionDigits: d || 0, maximumFractionDigits: d || 0 })),
    compact(n) {
      if (n == null) return '';
      if (numberUnit === 'full') return f.int(n);
      const a = Math.abs(n);
      const sign = n < 0 ? '-' : '';
      if (en) {
        if (a < 1000) return sign + fmtInt(Math.round(a), 'en');
        if (a < 1e6) return sign + trim(a / 1e3, a < 1e4 ? 1 : 0, lang) + 'K';
        if (a < 1e9) return sign + trim(a / 1e6, a < 1e7 ? 1 : 0, lang) + 'M';
        return sign + trim(a / 1e9, 1, lang) + 'B';
      }
      if (a < 1e4) return sign + fmtInt(Math.round(a), 'ko');
      if (a < 1e8) {
        const m = a / 1e4;
        return sign + (m < 100 ? trim(m, 1, lang) : fmtInt(Math.round(m), 'ko')) + '만';
      }
      return sign + trim(a / 1e8, 1, lang) + '억';
    },
    tok: (n) => (n == null ? '' : f.compact(n) + (en ? ' tokens' : ' 토큰')),
    usd(x) {
      if (x == null) return '';
      if (x > 0 && x < 0.01) return '<$0.01';
      return '$' + Number(x).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    pct: (x, d) => (x == null ? '' : trim(x, d || 0, lang) + '%'),
    dur(ms) {
      if (ms == null) return '';
      const sec = ms / 1000;
      const u = en ? { s: ' s', m: ' min', h: ' h' } : { s: '초', m: '분', h: '시간' };
      if (sec < 0.995) return trim(sec, 2, lang) + u.s;
      if (sec < 59.95) return trim(sec, 1, lang) + u.s;
      const whole = Math.round(sec);
      if (whole < 3600) {
        const mm = Math.floor(whole / 60);
        const ss = whole % 60;
        return mm + u.m + (ss ? ' ' + ss + u.s : '');
      }
      const min = Math.round(sec / 60);
      const hr = Math.floor(min / 60);
      const rm = min % 60;
      return hr + u.h + (rm ? ' ' + rm + u.m : '');
    },
    date: (ts) => dtf(lang, 'date', { month: 'long', day: 'numeric', weekday: 'short' }).format(ts),
    dateShort: (ts) => dtf(lang, 'dshort', { month: 'numeric', day: 'numeric' }).format(ts),
    dateYear: (ts) => dtf(lang, 'dyear', { year: 'numeric', month: 'short', day: 'numeric' }).format(ts),
    time: (ts) => dtf(lang, 'time', { hour: 'numeric', minute: '2-digit' }).format(ts),
    clock: (ts) => dtf(lang, 'clock', { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(ts),
    dateTime: (ts) => dtf(lang, 'dt', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(ts),
    full: (ts) => dtf(lang, 'full', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(ts),
    stamp(ts) {
      const p: Record<string, string> = {};
      dtf(lang, 'stamp', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
        .formatToParts(ts)
        .forEach((x) => {
          p[x.type] = x.value;
        });
      return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
    },
    rel(ts) {
      const diff = now() - ts;
      if (diff < 60_000) return en ? 'just now' : '방금';
      const m = Math.floor(diff / 60_000);
      if (m < 60) return en ? `${m} min ago` : `${m}분 전`;
      const hr = Math.floor(m / 60);
      if (hr < 24) return en ? `${hr} h ago` : `${hr}시간 전`;
      const d = Math.floor(hr / 24);
      if (d === 1) return en ? 'yesterday' : '어제';
      if (d < 30) return en ? `${d} days ago` : `${d}일 전`;
      return f.dateShort(ts);
    },
    range: (from, to) => t('period.range', { from: f.date(from), to: f.date(to) }),
    md: (ts) => dtf(lang, 'md' + (en ? 's' : 'l'), { month: en ? 'short' : 'long', day: 'numeric' }).format(ts),
    calendarDate(day, style = 'year') {
      const opts: Intl.DateTimeFormatOptions = style === 'date'
        ? { month: 'long', day: 'numeric', weekday: 'short' }
        : { month: style === 'md' && !en ? 'long' : 'short', day: 'numeric', ...(style === 'year' ? { year: 'numeric' as const } : {}) };
      return formatter(lang, 'calendar-' + style, opts, 'UTC').format(Date.parse(day + 'T00:00:00Z'));
    },
    rangeSum(from, to) {
      const a = parseYmd(from);
      const b = parseYmd(to);
      // Filter dates are calendar dates in the reporting zone, independent of display preferences.
      return t('period.sum', { from: f.calendarDate(from, 'md'), to: f.calendarDate(to, 'md'), n: Math.round((b - a) / DAY) + 1 });
    },
    bytes: (gb) => trim(gb, 1, lang) + ' GB',
  };
  return f;
}
